import React, { useState } from 'react';
import { Table, Button, Space, message, Modal, Tag, Image } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/axios';
import dayjs from 'dayjs';

interface AdminPosterDto {
    id: string;
    title: string;
    imageUrl: string;
    redirectUrl?: string;
    publishStartDate?: string;
    publishEndDate?: string;
    eventDate?: string;
    category: string;
    isActive: boolean;
    availableLanguages: string[];
}

export const PostersList: React.FC = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const { data, isLoading } = useQuery({
        queryKey: ['adminPosters', page, pageSize],
        queryFn: async () => {
            const response = await apiClient.get('/admin/posters', {
                params: { page, pageSize }
            });
            return response.data;
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiClient.delete(`/admin/posters/${id}`);
        },
        onSuccess: () => {
            message.success('Poster deleted successfully.');
            queryClient.invalidateQueries({ queryKey: ['adminPosters'] });
        },
        onError: () => {
            message.error('Failed to delete poster.');
        }
    });

    const handleDelete = (id: string) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this event/poster?',
            content: 'This action cannot be undone.',
            okText: 'Yes, Delete',
            okType: 'danger',
            onOk: () => deleteMutation.mutate(id)
        });
    };

    const columns = [
        {
            title: 'Image',
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            render: (url: string) => (
                <Image src={url} alt="poster" style={{ maxWidth: '80px', maxHeight: '50px', objectFit: 'cover' }} />
            )
        },
        { title: 'Title (Primary)', dataIndex: 'title', key: 'title' },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (cat: string) => (
                <Tag color={cat === 'youth' ? 'purple' : 'blue'}>
                    {cat === 'youth' ? 'Gençlik' : 'Genel'}
                </Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Draft'}</Tag>
            )
        },
        {
            title: 'Visibility Period',
            key: 'period',
            render: (_: any, record: AdminPosterDto) => {
                if (!record.publishStartDate && !record.publishEndDate) return <Tag color="default">Always</Tag>;
                const start = record.publishStartDate ? dayjs(record.publishStartDate).format('DD.MM.YYYY HH:mm') : 'Now';
                const end = record.publishEndDate ? dayjs(record.publishEndDate).format('DD.MM.YYYY HH:mm') : 'Forever';
                return <span style={{ fontSize: '12px' }}>{start} <br /> {end}</span>;
            }
        },
        {
            title: 'Event Date',
            dataIndex: 'eventDate',
            key: 'eventDate',
            render: (date?: string) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : <Tag>N/A</Tag>
        },
        {
            title: 'Translations',
            dataIndex: 'availableLanguages',
            key: 'availableLanguages',
            render: (langs: string[]) => (
                <Space size={[0, 8]} wrap>
                    {langs.map(l => <Tag key={l}>{l.toUpperCase()}</Tag>)}
                </Space>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: AdminPosterDto) => (
                <Space size="middle">
                    <Button icon={<EditOutlined />} onClick={() => navigate(`/admin/posters/edit/${record.id}`)} />
                    <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2>Events & Posters</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/admin/posters/create')}>
                    Create Poster
                </Button>
            </div>

            <Table
                dataSource={data?.data || []}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: data?.totalCount || 0,
                    onChange: (p, s) => { setPage(p); setPageSize(s); }
                }}
            />
        </div>
    );
};
